import styled from 'styled-components';
import React, {FC, useState, useEffect} from 'react';

import {convertHexToRGBA} from '../../../../../lib/color-utils';
import {useWindowScroll} from '../../../../../lib/hooks/useWindowScroll';
import {
  fontSizesMixins,
  fontWeights,
  mediaBreakpointDown,
  mediaQueryMixins,
  menuBreakpoint,
  palette,
  transition
} from '../../../../../lib/styleVariables';
import {paletteR4Secondary} from '../../../../../lib/styleVariablesR4';
import {PrimaryLink, SecondaryLink} from '../../../../molecules/buttons';
import {Icon} from '../../../../molecules/icon';
import {HeaderMenuProps} from '../headerMenuTypes';
import {StyledSubMenuWrapper as HeaderSubMenuWrapperDiv} from '../headerSubMenu';
import {getHeaderColor} from '../headerUtils';
import {HeaderExpandMobileMenu} from './expandMobileMenu';
import {HeaderLeftNav} from './leftNav';
import {useIsServer} from '../../../../../lib/hooks/useIsServer';

interface MainWrapperProps {
  isMenuOpen: boolean;
  backgroundColor: string;
  isScrolled: boolean;
  hasTransparentBgOnDark: boolean;
  $css?: string;
}

interface StyledLeftMenuButtonProps {
  backgroundColor?: string;
  isDropdown?: boolean;
  $css?: string;
}

const MAX_SCROLL_DISTANCE = 120;

const StyledMainWrapper = styled.div<MainWrapperProps>`
  top: 0;
  position: sticky;
  z-index: 2;
  width: 100%;
  background-color: ${props => props.backgroundColor};
  box-shadow: ${props =>
    (props.isScrolled ? `0px 4px 16px ${convertHexToRGBA(palette.black.base, 0.08)}` : 'none')};
  display: flex;
  flex-direction: column;
  align-items: center;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    margin: 0;
    width: 100%;
    bottom: ${props => (props.isMenuOpen ? '0' : 'initial')};
    height: ${props => (props.isMenuOpen ? '100vh' : '56px')};
  }
  transition: background-color 0.3s, box-shadow 0.3s;

  [data-theme='dark'] & {
    background: ${props => (!props.hasTransparentBgOnDark ? palette.black.highlight : 'transparent')};
    box-shadow: ${props => (!props.hasTransparentBgOnDark ? '0px 4px 16px rgba(0, 0, 0, 0.32)' : 'none')};
    ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
      background: ${palette.black.highlight};
    }
  }
  ${props => props.$css}
`;

const SpacingElement = styled.div<{backgroundColor: string}>`
  height: 24px;
  width: 100%;
  background-color: ${props => props.backgroundColor};
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    display: none;
  }
  [data-theme='dark'] & {
    background: transparent;
  }
`;

const StyledHeaderMenuWrapper = styled.div<{isMenuOpen: boolean; $css?: string}>`
  height: 72px;
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 2;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    width: 100%;
    height: 56px;
  }
  ${props => !props.isMenuOpen && props.$css}
`;

const StyledHeaderMenu = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  max-width: 1272px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
`;

const StyledLogoContainer = styled.div`
  display: flex;
  align-items: center;
  z-index: 1;
`;

const StyledMenuItems = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-left: 32px;
  width: 100%;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    display: none;
  }
`;

const StyledRightMenuContainer = styled.div`
  align-items: center;
  display: flex;
`;

export const StyledLeftMenuButton = styled.a<StyledLeftMenuButtonProps>`
  display: inline-block;
  text-decoration: none;
  background-color: ${props => props.backgroundColor || palette.white.highlight};
  font-weight: ${fontWeights.bold} !important;
  color: ${palette.black.base};
  padding: ${props => (props.isDropdown ? '8px 13px' : '8px 16px')};
  border-radius: 28px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.3s;

  [data-theme='dark'] & {
    background: transparent;
    color: ${palette.white.highlight};
  }

  &:hover {
    color: ${paletteR4Secondary.blue.lowlight};
    ${HeaderSubMenuWrapperDiv} {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
  }
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    padding: 8px;
    border-radius: 0px;
    background: transparent;
  }
  ${props => props.$css}
`;

export const rightMenuButtonStyle = `
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: ${fontWeights.bold} !important;
  margin-left: 16px;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    justify-content: center;
    margin-left: 0;
    margin-bottom: 16px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const commonButtonStyle = `
  ${fontSizesMixins.text3()}
  transition: ${transition.fast};
  ${mediaBreakpointDown('desktop')} {
    padding: 8px 10px;
    ${fontSizesMixins.text4()}
  }
`;

export const StyledRightMenuTertiaryButton = styled.a`
  display: inline-block;
  text-decoration: none;
  margin-right: 8px;
  ${fontSizesMixins.text3()};
  color: ${palette.black.base};

  [data-theme='dark'] & {
    color: ${palette.white.highlight};
  }

  &:hover {
    color: ${paletteR4Secondary.blue.lowlight};
  }

  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    padding: 8px;
    margin: 0px;
    margin-bottom: 16px;
  }
  ${commonButtonStyle}
`;

const StyledMobileMenuIcon = styled.div`
  display: none;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    display: flex;
    justify-content: flex-end;
  }
`;

const StyledHeaderLeftContainer = styled.div`
  margin-right: 45px;
  ${mediaBreakpointDown('desktop')} {
    margin-right: 0;
  }
`;

const StyledLogoIcon = styled(Icon)`
  width: 150px;
  height: 48px;
  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    width: 115px;
    height: 26px;
  }
`;

const StyledPrimaryLink = styled(PrimaryLink)`
  ${commonButtonStyle}
  ${rightMenuButtonStyle}
`;

const StyledSecondaryLink = styled(SecondaryLink)`
  [data-theme='dark'] & {
    box-shadow: inset 0 0 0 3px ${palette.white.highlight};
    color: ${palette.white.highlight};
    transition-property: background, color, box-shadow;

    &:hover {
      background: ${palette.white.highlight};
      color: ${palette.black.base};
    }
  }
  ${commonButtonStyle}
  ${rightMenuButtonStyle}
`;

export const HeaderMenu: FC<HeaderMenuProps> = props => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {scrollY} = useWindowScroll();
  const isServer = useIsServer();

  useEffect(() => {
    // Page has to be scrollable again if the location changes
    document.body.style.overflow = 'initial';
  }, [props.locationPath]);

  const onMenuIconClick = () => {
    // user shouldn't be able to scroll on body when mobile menu is open
    document.body.style.overflow = isMenuOpen ? 'initial' : 'hidden';
    setIsMenuOpen(!isMenuOpen);
  };

  const isScrolled = scrollY > MAX_SCROLL_DISTANCE;
  const headerBackgroundColor = getHeaderColor({
    isHighlighted: props.isHighlighted && !isMenuOpen,
    isScrolled
  }).header;

  const spacingElement = (
    <SpacingElement className={props.className} backgroundColor={headerBackgroundColor} />
  );

  return (
    <>
      {spacingElement}
      <StyledMainWrapper
        backgroundColor={headerBackgroundColor}
        className={props.className}
        isMenuOpen={isMenuOpen}
        isScrolled={isScrolled}
        hasTransparentBgOnDark={!isServer && !isScrolled}
        $css={props.mainWrapperCss}
      >
        <StyledHeaderMenuWrapper isMenuOpen={isMenuOpen} $css={props.menuWrapperCss}>
          <StyledHeaderMenu>
            <StyledLogoContainer>
              <a href="/">
                <StyledLogoIcon name="frontLogo" color={palette.frontier_red.base} />
              </a>
            </StyledLogoContainer>
            <StyledMobileMenuIcon>
              <Icon name={isMenuOpen ? 'exit' : 'hamburgerMenu'} onClick={onMenuIconClick} />
            </StyledMobileMenuIcon>
            <StyledMenuItems>
              <StyledHeaderLeftContainer>
                <HeaderLeftNav
                  isHeaderHighlighted={props.isHighlighted}
                  isScrolled={isScrolled}
                  leftNav={props.leftNav}
                  locationPath={props.locationPath}
                />
              </StyledHeaderLeftContainer>
              <StyledRightMenuContainer>
                {props.rightNav.map(item => (
                  <StyledRightMenuTertiaryButton href={item.href} key={item.name}>
                    {item.name}
                  </StyledRightMenuTertiaryButton>
                ))}
                <StyledSecondaryLink href={props.secondaryItem.href}>
                  {props.secondaryItem.name}
                </StyledSecondaryLink>
                <StyledPrimaryLink href={props.primaryItem.href}>{props.primaryItem.name}</StyledPrimaryLink>
              </StyledRightMenuContainer>
            </StyledMenuItems>
          </StyledHeaderMenu>
        </StyledHeaderMenuWrapper>
        {isMenuOpen && (
          <HeaderExpandMobileMenu
            leftNav={props.leftNav}
            rightNav={props.rightNav}
            primaryItem={props.primaryItem}
            secondaryItem={props.secondaryItem}
            locationPath={props.locationPath}
          />
        )}
      </StyledMainWrapper>
      {spacingElement}
    </>
  );
};
